import { useEffect } from "react";

import { ArrowLeft, Briefcase, Users, FolderKanban, MessageSquare, Brain, Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { useOnboardingTracking } from "@/hooks/useOnboardingTracking";
import { ROUTES } from "@/lib/routes";

const QuickStartGuide = () => {
  const navigate = useNavigate();
  const { trackEvent } = useOnboardingTracking();

  // Enable keyboard shortcuts for back navigation
  useKeyboardNavigation({
    onBack: () => navigate(ROUTES.BOARDS_CREATE),
  });

  // Track when user views the quick start guide
  useEffect(() => {
    trackEvent("quick_start_viewed");
  }, [trackEvent]);

  const handleCreateBoard = () => {
    sessionStorage.removeItem("skipDashboardRedirect");
    navigate(ROUTES.BOARDS_CREATE);
  };

  const handleExploreDashboard = () => {
    navigate(ROUTES.HOME);
  };

  const concepts = [
    {
      icon: Briefcase,
      title: "Boards",
      description:
        "Think of a board as your personal advisory council for a specific business or challenge. Each board contains expert personas tailored to your needs.",
    },
    {
      icon: Users,
      title: "Personas",
      description:
        "AI-powered advisors with specific roles, expertise, and perspectives. They maintain consistent personalities and remember your context across conversations.",
    },
    {
      icon: FolderKanban,
      title: "Projects",
      description:
        "Track your work streams and initiatives. Your board members stay informed about active projects and provide relevant guidance.",
    },
    {
      icon: MessageSquare,
      title: "Sessions",
      description:
        "Conversations where you consult your advisors. Each session maintains context and you can bring multiple personas into the discussion.",
    },
    {
      icon: Brain,
      title: "Memories",
      description:
        "Important information your advisors remember across sessions - your goals, constraints, preferences, and key decisions.",
    },
  ];

  const steps = [
    "Create your first advisory board - choose a pre-built template or build a custom one",
    "Define your personas - AI experts who will provide guidance specific to your needs",
    "Start a session - ask questions, get advice, and collaborate with your advisory board",
    "Create projects - organize your work and keep your advisors informed about your initiatives",
    "Build memories - help your advisors remember important context for better guidance over time",
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.BOARDS_CREATE)} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Board Creation
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center space-y-4 mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight">Welcome to AdvisorMuse</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Your AI-powered thinking support platform. Get expert guidance for complex decisions and everyday overwhelm
            alike.
          </p>
        </div>

        {/* What is AdvisorMuse */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">What is AdvisorMuse?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              AdvisorMuse helps you reclaim your mental energy by providing AI-powered thinking support for everything
              from high-stakes decisions to the pile of daily admin that's draining your spoons.
            </p>
            <p>
              Instead of carrying everything in your head, create a virtual thinking board with diverse expertise that
              helps you offload cognitive load and focus on what only you can do.
            </p>
          </CardContent>
        </Card>

        {/* Key Concepts */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-6">Key Concepts</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {concepts.map((concept) => {
              const Icon = concept.icon;
              return (
                <Card key={concept.title} className="hover:border-primary/50 transition-colors">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{concept.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed">{concept.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* How It Works */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-4">
              {steps.map((step, index) => (
                <li key={index} className="flex gap-4">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-semibold shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-muted-foreground pt-1">{step}</p>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {/* Benefits */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Why Use AdvisorMuse?</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1">✓</span>
                <span>
                  <strong>Save Your Spoons:</strong> Offload mental overhead to AI so you can focus on strategic work
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1">✓</span>
                <span>
                  <strong>Handle The Pile:</strong> Get support for everyday admin and complex decisions alike
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1">✓</span>
                <span>
                  <strong>Consistent Context:</strong> Stop re-explaining - your board remembers everything
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1">✓</span>
                <span>
                  <strong>Available 24/7:</strong> Get thinking support whenever overwhelm hits
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1">✓</span>
                <span>
                  <strong>Tailored Support:</strong> Build boards specific to your unique challenges and energy levels
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Getting Started Tips */}
        <Card className="mb-8 border-primary/50">
          <CardHeader>
            <CardTitle className="text-2xl">Getting Started Tips</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-muted-foreground">
            <p>
              <strong>Start With What's Overwhelming:</strong> Use pre-built templates for common scenarios or describe
              what's draining your spoons right now.
            </p>
            <p>
              <strong>Offload Context:</strong> The more you share about what you're juggling, the better your board can
              help lighten the load.
            </p>
            <p>
              <strong>Create Memories:</strong> Save your mental bandwidth - let your board remember the details so you
              don't have to.
            </p>
            <p>
              <strong>Iterate:</strong> Your energy and needs change. Adjust your board as you go - add support where
              you're stretched thin.
            </p>
          </CardContent>
        </Card>

        {/* Call to Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button size="lg" onClick={handleCreateBoard} className="gap-2 min-w-[200px]">
            Create My First Board
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="lg" onClick={handleExploreDashboard} className="min-w-[200px]">
            Explore Dashboard
          </Button>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Don't worry, you can always come back to this guide later
        </p>
      </main>
    </div>
  );
};

export default QuickStartGuide;
